import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import eligibilityCheckServices from "../../../../services/api/service/eligibilityCheckService";
import { decrypt } from "../../../../utils/crypto";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import { ALLOWED_EXTENSIONS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from "../constants";
import { UploadVia } from "../types";
import { validateImageFile, ImageType } from "../../../../utils/imageValidationUtils";

export function useDocumentUpload({
  dispatch,
  tabIndex,
  applicants,
  submissionTrackingId,
  applicationNumber,
  applicationId,
  userProfile,
  fetchUserApplicationData,
  coApplicantDetails,
  partnerStartedJourney,
  findCoApplicantNumber,
  formUserData,
  refreshDocuments,
}: {
  dispatch: any;
  tabIndex: number;
  applicants: string[];
  submissionTrackingId: any;
  applicationNumber: any;
  applicationId?: string;
  userProfile: any;
  fetchUserApplicationData: any;
  coApplicantDetails: any;
  partnerStartedJourney: boolean;
  findCoApplicantNumber: number;
  formUserData: any;
  refreshDocuments: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadDocumentLoader, setUploadDocumentLoader] = useState<any>([]);
  const [uploadDocumentLoading, setUploadDocumentLoading] = useState(false);

  const [docId, setDocId] = useState<number>(0);
  const [docTypeItem, setDocTypeItem] = useState<any>(null);
  const [docType, setDocType] = useState<string>("");
  const [docName, setDocName] = useState<string>("");
  const [uploadedVia, setUploadedVia] = useState<UploadVia>(UploadVia.Manual);

  const [coreExamList, setCoreExamList] = useState<any>([]);
  const [languageExamList, setLanguageExamList] = useState<any>([]);
  const [dropdownData, setDropdownData] = useState<{
    languageExamTypes: any[];
    entranceExamTypes: any[];
  }>({
    languageExamTypes: [],
    entranceExamTypes: [],
  });

  useEffect(() => {
    if (!formUserData) return;

    const result = Object.keys(formUserData)
      .filter((key) => key.startsWith("coreExamID"))
      .map((key) => {
        const suffix = key.split("coreExamID")[1] || "";
        const id = formUserData[key];
        const name = formUserData[`coreExamName${suffix}`];
        return { id: Number(id), value: name };
      });

    const resultLanguage = Object.keys(formUserData)
      .filter((key) => key.startsWith("languageExamID"))
      .map((key) => {
        const suffix = key.split("languageExamID")[1] || "";
        const id = formUserData[key];
        const name = formUserData[`languageExamName${suffix}`];
        return { id: Number(id), value: name };
      });

    setCoreExamList(result);
    setLanguageExamList(resultLanguage);
  }, [formUserData]);

  const fetchSystemValues = async () => {
    try {
      const systemValuesResponse =
        await eligibilityCheckServices.getSystemValues();

      if (systemValuesResponse.data?.status === "success") {
        const systemValues = systemValuesResponse.data.data;

        // Filter and process Language Exam Types
        const languageExamTypes = systemValues
          .filter(
            (item: any) => item.name === "LANGUAGE_EXAM_TYPE" && item.active
          )
          .map((item: any) => ({
            value: item.code,
            label: item.value,
            id: item.serialId,
          }));

        // Filter and process Entrance Exam Types
        const entranceExamTypes = systemValues
          .filter(
            (item: any) => item.name === "ENTRANCE_EXAM_TYPE" && item.active
          )
          .map((item: any) => ({
            value: item.code,
            label: item.value,
            id: item.serialId,
          }));

        // Update dropdown data with system values
        setDropdownData((prev) => ({
          ...prev,
          languageExamTypes,
          entranceExamTypes,
        }));
      }
    } catch (error) {
      console.error("Error fetching system values:", error);
    }
  };

  useEffect(() => {
    fetchSystemValues();
  }, []);

  const handleUploadDocument = useCallback((nextDocId: any, item: any) => {
    if (item?.uploadedVia === "account_agreegator") {
      setUploadedVia(UploadVia.Account_Aggregator_Manual);
    } else {
      setUploadedVia(UploadVia.Manual);
    }

    setDocId(Number(nextDocId));
    setDocTypeItem(item);

    setDocType(item?.docNameDms || "");
    setDocName(item?.docName?.replace(/\s+/g, "").toUpperCase() || "");

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleUploadDocumentSubmit = useCallback(
    async (base64Data: string, fileFormat: string) => {
      try {
        setUploadDocumentLoading(true);
        const oldUploadDocumentLoader = [...uploadDocumentLoader];

        const payload = {
          formSubmissionTrackingId: submissionTrackingId,
          userId: Number(decrypt(userProfile?.id)),
          documentId: String(docId),
          uploadedVia,
          fileFormat,
          userType:
            tabIndex === 0
              ? "Applicant"
              : applicants?.[tabIndex],
          base64Data,
        };

        oldUploadDocumentLoader.push(docId);
        setUploadDocumentLoader(oldUploadDocumentLoader);

        const response: any = await eligibilityCheckServices.uploadDocument(payload);

        if (response?.status === 201 || response?.status === 200) {
          try {
            let pushPayload: any = {
              eventName:
                tabIndex === 0
                  ? `APPLICANT_${docName}_PUSH_DOCUMENT`
                  : `COAPPLICANT${findCoApplicantNumber}_${docName}_PUSH_DOCUMENT`,
              userApplicantType:
                tabIndex === 0
                  ? "Applicant"
                  : `Co-Applicant ${findCoApplicantNumber}`,
              trackingId: submissionTrackingId,
              docType,
              applicantId:
                tabIndex === 0
                  ? Number(fetchUserApplicationData?.applicantId)
                  : partnerStartedJourney
                    ? Number(
                      coApplicantDetails?.[
                        `coapplicant${findCoApplicantNumber}` as keyof typeof coApplicantDetails
                      ]?.coApplicantId ?? ""
                    )
                    : Number(localStorage.getItem("coAppId")),
              applicationId: applicationId
                ? Number(applicationId)
                : Number(fetchUserApplicationData?.applicationId),
              documentFilePath: response?.data?.data?.url || "",
            };

            if (docTypeItem?.docName === "Class X Marksheet") {
              pushPayload.appEducationId = 283;
            } else if (docTypeItem?.docName === "Class XII Marksheet") {
              pushPayload.appEducationId = 284;
            } else if (docTypeItem?.docName === "Graduation Marksheets") {
              pushPayload.appEducationId = 286;
            } else if (docTypeItem?.docName === "Diploma") {
              pushPayload.appEducationId = 285;
            }

            const examNameFrom = docTypeItem?.docName?.split("-")?.pop()?.trim?.();

            const matchcoreExam = dropdownData?.entranceExamTypes?.find(
              (item: any) => item.value === examNameFrom
            );
            const matchLanguageExam: any = dropdownData?.languageExamTypes?.find(
              (item: any) => item.value === examNameFrom
            );


            if (matchcoreExam) pushPayload.serialId = matchcoreExam?.id;
            if (
              matchLanguageExam != undefined &&
              matchLanguageExam != null &&
              matchLanguageExam != ""
            ) {
              pushPayload.serialId = matchLanguageExam?.id;
            }

            if (docTypeItem?.docName === "PANCR" || docTypeItem?.docName === "PAN") {
              pushPayload.applicantHasPanCardFlag = true;
            }

            const canPush =
              Boolean(docType) &&
              ((tabIndex === 0 &&
                pushPayload?.applicantId > 0 &&
                pushPayload?.applicationId > 0) ||
                (tabIndex > 0 && pushPayload?.applicationId > 0));

            if (canPush) {
              await eligibilityCheckServices.pushDocument(pushPayload);
            }
          } catch {
            // Push document failures should not block upload UX
          }

          setDocId(0);
          refreshDocuments();

          const nextLoader = [...oldUploadDocumentLoader];
          const idx = nextLoader.indexOf(docId);
          if (idx >= 0) nextLoader.splice(idx, 1);
          setUploadDocumentLoader(nextLoader);
        }
      } catch (error: any) {
        const nextLoader = [...uploadDocumentLoader];
        const idx = nextLoader.indexOf(docId);
        if (idx >= 0) nextLoader.splice(idx, 1);
        setUploadDocumentLoader(nextLoader);

        dispatch(
          setStatusPopup({
            status: "error",
            message: error?.response?.data?.message ?? 
              "Please select a valid image (JPEG, PNG) or PDF file (max 5MB).",
          })
        );
      } finally {
        setUploadDocumentLoading(false);
      }
    },
    [
      uploadDocumentLoader,
      submissionTrackingId,
      userProfile,
      docId,
      uploadedVia,
      tabIndex,
      applicants,
      findCoApplicantNumber,
      docName,
      docType,
      fetchUserApplicationData,
      partnerStartedJourney,
      coApplicantDetails,
      applicationId,
      docTypeItem,
      coreExamList,
      languageExamList,
      dropdownData,
      dispatch,
      refreshDocuments,
    ]
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];

      // Check file size first
      if (file.size > MAX_FILE_SIZE) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "File size must be less than 5MB.",
          })
        );
        return;
      }

      // Check if file is empty
      if (file.size === 0) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "File is empty. Please select a valid file.",
          })
        );
        return;
      }

      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      const isPdf = fileExtension === "pdf";

      if (isPdf) {
        // For PDF, use MIME type validation (PDF magic bytes validation can be added)
        const fileType = (file.type || "").toLowerCase();
        if (fileType !== "application/pdf" && fileExtension !== "pdf") {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Please select a valid PDF file.",
            })
          );
          return;
        }

        // Process PDF
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64String = e.target?.result as string;
          if (!base64String || !base64String.startsWith("data:")) {
            dispatch(
              setStatusPopup({
                status: "error",
                message: "Failed to process the file. Please try again.",
              })
            );
            return;
          }

          const base64Data = base64String.split(",")[1];
          if (!base64Data) {
            dispatch(
              setStatusPopup({
                status: "error",
                message: "Failed to process the file. Please try again.",
              })
            );
            return;
          }
          handleUploadDocumentSubmit(base64Data, "pdf");
        };

        reader.onerror = () => {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Error reading the file. Please try again.",
            })
          );
        };

        reader.readAsDataURL(file);
      } else {
        // For images, use secure magic bytes validation
        try {
          const validation = await validateImageFile(file, MAX_FILE_SIZE);

          if (!validation.isValid || !validation.type) {
            dispatch(
              setStatusPopup({
                status: "error",
                message:
                  validation.error ||
                  "File is not a valid image format. Only PNG, JPEG, WEBP, and GIF are allowed.",
              })
            );
            return;
          }

          // Extract validated image type (guaranteed to be non-null after check above)
          const imageType: ImageType = validation.type;
          const fileFormat = imageType === "jpeg" ? "jpg" : imageType;

          // Valid image detected - process it
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64String = e.target?.result as string;
            if (!base64String || !base64String.startsWith("data:")) {
              dispatch(
                setStatusPopup({
                  status: "error",
                  message: "Failed to process the file. Please try again.",
                })
              );
              return;
            }

            const base64Data = base64String.split(",")[1];
            if (!base64Data) {
              dispatch(
                setStatusPopup({
                  status: "error",
                  message: "Failed to process the file. Please try again.",
                })
              );
              return;
            }
            handleUploadDocumentSubmit(base64Data, fileFormat);
          };

          reader.onerror = () => {
            dispatch(
              setStatusPopup({
                status: "error",
                message: "Error reading the file. Please try again.",
              })
            );
          };

          reader.readAsDataURL(file);
        } catch (error) {
          console.error("Error validating image:", error);
          dispatch(
            setStatusPopup({
              status: "error",
              message: "An error occurred while validating the file. Please try again.",
            })
          );
        }
      }

      // Reset input
      if (event.target) {
        event.target.value = "";
      }
    },
    [dispatch, handleUploadDocumentSubmit]
  );

  return {
    fileInputRef,
    uploadDocumentLoader,
    uploadDocumentLoading,
    docId,
    uploadedVia,
    handleUploadDocument,
    handleFileChange,
  };
}

